import { useSelector } from 'react-redux';

import { ItemAvailabilityStatus } from 'entities/CatalogItem/lib/itemAvailability';
import { CatalogItemSource } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { getUserIsMaintenanceTechnician } from 'entities/User/model/selectors/userSelectors';
import { CatalogItemCheckbox } from 'features/CatalogItemCheckbox';
import { UnavailableTooltipContent } from 'shared/ui/ItemAvailabilityModal';
import { DynamicTooltip } from 'shared/ui/Tooltip/ui/DynamicTooltip';

export interface CatalogItemCheckboxSectionProps {
    showQtyCheckbox: boolean;
    isActive: boolean;
    qty: number;
    itemIdToEdit: string | undefined;
    /** Key in `selectedItems` (see `CatalogItemCheckbox` / `getItemUid`). */
    selectionKey: string;
    itemUid: string;
    catalogItemId?: string;
    quickEdit: boolean | undefined;
    isInOrder: boolean | undefined;
    isMoveToInventoryMode?: boolean;
    showUnavailableTooltip: boolean;
    availabilityStatus?: ItemAvailabilityStatus;
    onLearnMoreActionRequired: () => void;
    isAvailable?: boolean;
    source?: CatalogItemSource;
    /** Browse/selection category for `addNewItem` tracking; not the row's property catalog category. */
    categoryId?: string;
    subCategoryId?: string;
    title?: string;
    propertyCategoryId?: string;
}

export const CatalogItemCheckboxSection = ({
    showQtyCheckbox,
    isActive,
    qty,
    itemIdToEdit,
    selectionKey,
    itemUid,
    catalogItemId,
    quickEdit,
    isInOrder,
    isMoveToInventoryMode = false,
    showUnavailableTooltip,
    availabilityStatus,
    onLearnMoreActionRequired,
    isAvailable = true,
    source,
    categoryId,
    subCategoryId,
    title,
    propertyCategoryId,
}: CatalogItemCheckboxSectionProps) => {
    const isMaintenanceTechnician = useSelector(getUserIsMaintenanceTechnician);

    if (showUnavailableTooltip) {
        return (
            <DynamicTooltip
                maxWidth={400}
                interactive
                content={(closeTooltip) => (
                    <UnavailableTooltipContent
                        availabilityStatus={availabilityStatus}
                        onLearnMore={
                            isMoveToInventoryMode
                                ? undefined
                                : () => {
                                      onLearnMoreActionRequired();
                                      closeTooltip();
                                  }
                        }
                        hideReplacementLink={
                            isMoveToInventoryMode || isMaintenanceTechnician
                        }
                    />
                )}
            >
                <CatalogItemCheckbox
                    showQtyCheckbox={showQtyCheckbox}
                    isActive={false}
                    qty={qty}
                    itemIdToEdit={itemIdToEdit}
                    selectionKey={selectionKey}
                    itemUid={itemUid}
                    catalogItemId={catalogItemId}
                    quickEdit={quickEdit}
                    isInOrder={isInOrder}
                    disabled
                    isAvailable={isAvailable}
                    source={source}
                    categoryId={categoryId}
                    subCategoryId={subCategoryId}
                    title={title}
                    propertyCategoryId={propertyCategoryId}
                />
            </DynamicTooltip>
        );
    }

    return (
        <CatalogItemCheckbox
            showQtyCheckbox={showQtyCheckbox}
            isActive={isActive}
            qty={qty}
            itemIdToEdit={itemIdToEdit}
            selectionKey={selectionKey}
            itemUid={itemUid}
            catalogItemId={catalogItemId}
            quickEdit={quickEdit}
            isInOrder={isInOrder}
            isAvailable={isAvailable}
            source={source}
            categoryId={categoryId}
            subCategoryId={subCategoryId}
            title={title}
            propertyCategoryId={propertyCategoryId}
        />
    );
};
